import { Head, Link, usePage } from "@inertiajs/react"
import {
  ArrowRight,
  Briefcase,
  Building2,
  Eye,
  MapPin,
  Search,
  Trophy,
  Upload,
  UserCheck,
  Users,
} from "lucide-react"
import { type RefObject, useEffect, useRef, useState } from "react"

import MobileOnboarding from "@/components/home/mobile-onboarding"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { signInPath, signUpPath } from "@/routes"

// ─── Data ────────────────────────────────────────────────────────────

const STATS = [
  {
    label: "Jugadores activos",
    target: 2500,
    suffix: "+",
    icon: Users,
    accent: "text-krown-200",
  },
  {
    label: "Oportunidades abiertas",
    target: 150,
    suffix: "+",
    icon: Briefcase,
    accent: "text-krown-crown",
  },
  {
    label: "Clubes y academias",
    target: 80,
    suffix: "+",
    icon: Building2,
    accent: "text-krown-accent",
  },
]

const STEPS = [
  {
    number: "01",
    icon: Upload,
    title: "Creá tu perfil",
    description:
      "Armá tu perfil profesional con tus stats, highlights y videos de tus mejores jugadas.",
    badge: "Jugadores",
    accentBg: "bg-krown-200/10",
    accentBorder: "border-krown-200/20",
    accentText: "text-krown-200",
  },
  {
    number: "02",
    icon: Search,
    title: "Descubrí talento",
    description:
      "Filtrá jugadores por posición, ubicación y rendimiento. Guardá tus favoritos y seguí su evolución.",
    badge: "Scouts",
    accentBg: "bg-krown-crown/10",
    accentBorder: "border-krown-crown/20",
    accentText: "text-krown-crown",
  },
  {
    number: "03",
    icon: UserCheck,
    title: "Conectá y reclutá",
    description:
      "Publicá oportunidades, contactá jugadores y gestioná pruebas desde la plataforma.",
    badge: "Clubes",
    accentBg: "bg-krown-accent/10",
    accentBorder: "border-krown-accent/20",
    accentText: "text-krown-accent",
  },
]

const FEATURED_PLAYERS = [
  {
    name: "Matías González",
    initials: "MG",
    position: "Delantero Centro",
    city: "Buenos Aires",
    country: "Argentina",
    goals: 18,
    assists: 7,
    views: 1240,
  },
  {
    name: "Lucas Rodríguez",
    initials: "LR",
    position: "Mediocampista",
    city: "Montevideo",
    country: "Uruguay",
    goals: 5,
    assists: 14,
    views: 890,
  },
  {
    name: "Santiago López",
    initials: "SL",
    position: "Defensa Central",
    city: "Santiago",
    country: "Chile",
    goals: 2,
    assists: 3,
    views: 650,
  },
  {
    name: "Diego Fernández",
    initials: "DF",
    position: "Portero",
    city: "Rosario",
    country: "Argentina",
    goals: 0,
    assists: 1,
    views: 780,
  },
  {
    name: "Valentín Torres",
    initials: "VT",
    position: "Extremo Derecho",
    city: "Córdoba",
    country: "Argentina",
    goals: 12,
    assists: 9,
    views: 1560,
  },
]

const TESTIMONIALS = [
  {
    quote:
      "Gracias a KrownFT conseguí mi primera prueba en un club de primera división. Me dio la visibilidad que necesitaba.",
    name: "Nicolás Pereyra",
    role: "Jugador — Delantero",
    initials: "NP",
  },
  {
    quote:
      "Encontramos tres jugadores para nuestra cantera en menos de dos semanas. La búsqueda avanzada nos ahorró muchísimo tiempo.",
    name: "Marcelo Ruiz",
    role: "Scout — Club Atlético Nacional",
    initials: "MR",
  },
  {
    quote:
      "La calidad de los perfiles y la información de cada jugador es impresionante. Es como tener un scouting digital completo.",
    name: "Carolina Méndez",
    role: "Directora Deportiva",
    initials: "CM",
  },
]

// ─── Hooks & Helpers ─────────────────────────────────────────────────

function useInView(ref: RefObject<HTMLElement | null>, threshold = 0.15) {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true)
          io.disconnect()
        }
      },
      { threshold },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [ref, threshold])
  return visible
}

function AnimatedCounter({
  target,
  suffix = "",
}: {
  target: number
  suffix?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const visible = useInView(ref)
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!visible) return
    const ms = 1800
    const t0 = Date.now()
    const tick = () => {
      const p = Math.min((Date.now() - t0) / ms, 1)
      setCount(Math.floor((1 - Math.pow(1 - p, 3)) * target))
      if (p < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [visible, target])

  return (
    <span ref={ref}>
      {count.toLocaleString("es-AR")}
      {suffix}
    </span>
  )
}

function Reveal({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode
  className?: string
  delay?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const visible = useInView(ref)
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
      } ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────

export default function Landing() {
  const { props } = usePage()
  const isMobileApp = (props.app as { mobile_app?: boolean })?.mobile_app

  if (isMobileApp) return <MobileOnboarding />

  return <DesktopLanding />
}

function DesktopLanding() {
  const [heroReady, setHeroReady] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setHeroReady(true))
    return () => cancelAnimationFrame(id)
  }, [])

  return (
    <>
      <Head title="KrownFT — Donde el talento se encuentra con la oportunidad" />

      <div className="bg-background flex min-h-svh flex-col">
        {/* ── Header ─────────────────────────────────────── */}
        <header className="border-border bg-background/80 safe-area-top sticky top-0 z-50 flex items-center justify-between border-b px-6 py-4 backdrop-blur-md">
          <Link href="/" className="flex items-center">
            <img
              src="/images/krown_logo_header.png"
              alt="KrownFT"
              className="h-8"
            />
          </Link>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              className="text-white hover:bg-white/5"
              asChild
            >
              <Link href={signInPath()}>Iniciar sesión</Link>
            </Button>
            <Button variant="accent" asChild>
              <Link href={signUpPath()}>Registrarse</Link>
            </Button>
          </div>
        </header>

        {/* ── Hero ───────────────────────────────────────── */}
        <section className="relative overflow-hidden px-6 py-20 md:py-28 lg:py-36">
          {/* Ambient glows + football pitch center circle */}
          <div className="pointer-events-none absolute inset-0">
            <div className="bg-krown-200 absolute -top-32 left-1/2 h-[500px] w-[700px] -translate-x-1/2 rounded-full opacity-[0.07] blur-[120px]" />
            <div className="bg-krown-accent absolute top-1/3 -right-24 h-[300px] w-[300px] rounded-full opacity-[0.04] blur-[100px]" />
            <div className="bg-krown-crown absolute bottom-0 -left-24 h-[250px] w-[250px] rounded-full opacity-[0.05] blur-[80px]" />
            {/* Center circle — football pitch evocation */}
            <div className="border-krown-200/[0.06] absolute top-1/2 left-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full border" />
            <div className="bg-krown-200/[0.08] absolute top-1/2 left-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full" />
          </div>

          <div className="relative mx-auto max-w-4xl text-center">
            {/* Badge */}
            <div
              className={`border-krown-200/20 bg-krown-200/10 mb-8 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 transition-all duration-700 ${
                heroReady
                  ? "translate-y-0 opacity-100"
                  : "translate-y-4 opacity-0"
              }`}
            >
              <Trophy className="text-krown-crown h-3.5 w-3.5" />
              <span className="text-krown-100 text-[11px] font-semibold tracking-wider uppercase">
                La plataforma del fútbol profesional
              </span>
            </div>

            {/* Headline */}
            <h1
              className={`mb-6 text-4xl leading-tight font-bold text-white transition-all duration-700 md:text-5xl lg:text-6xl ${
                heroReady
                  ? "translate-y-0 opacity-100"
                  : "translate-y-6 opacity-0"
              }`}
              style={{ transitionDelay: "150ms" }}
            >
              Donde el{" "}
              <span className="from-krown-100 to-krown-accent bg-gradient-to-r bg-clip-text text-transparent">
                talento
              </span>{" "}
              se encuentra
              <br className="hidden sm:block" /> con la oportunidad
            </h1>

            {/* Subtitle */}
            <p
              className={`text-muted-foreground mx-auto mb-10 max-w-2xl text-lg transition-all duration-700 md:text-xl ${
                heroReady
                  ? "translate-y-0 opacity-100"
                  : "translate-y-6 opacity-0"
              }`}
              style={{ transitionDelay: "300ms" }}
            >
              Creá tu perfil profesional, subí tus highlights y conectá con
              scouts y clubes de todo el mundo. Tu próxima oportunidad empieza
              acá.
            </p>

            {/* CTAs */}
            <div
              className={`flex flex-col items-center gap-4 transition-all duration-700 sm:flex-row sm:justify-center ${
                heroReady
                  ? "translate-y-0 opacity-100"
                  : "translate-y-6 opacity-0"
              }`}
              style={{ transitionDelay: "450ms" }}
            >
              <Button
                size="lg"
                variant="cta"
                className="group gap-2 px-8 text-base"
                asChild
              >
                <Link href={signUpPath()}>
                  Soy Jugador
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="brand-outline"
                className="px-8 text-base"
                asChild
              >
                <Link href={signUpPath()}>Busco Talento</Link>
              </Button>
            </div>

            {/* Social proof strip */}
            <div
              className={`mt-14 flex items-center justify-center gap-3 transition-all duration-700 ${
                heroReady ? "opacity-100" : "opacity-0"
              }`}
              style={{ transitionDelay: "700ms" }}
            >
              <div className="flex -space-x-2">
                {["MG", "LR", "SL", "VT"].map((initials) => (
                  <div
                    key={initials}
                    className="border-background bg-krown-200 flex h-7 w-7 items-center justify-center rounded-full border-2 text-[9px] font-bold text-white"
                  >
                    {initials}
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground text-xs">
                +2,500 jugadores en 12 países
              </p>
            </div>
          </div>
        </section>

        {/* ── Stats ──────────────────────────────────────── */}
        <section className="border-border border-y">
          <div className="mx-auto grid max-w-5xl grid-cols-1 sm:grid-cols-3">
            {STATS.map((stat, i) => (
              <Reveal
                key={stat.label}
                delay={i * 100}
                className={`flex flex-col items-center gap-2 px-6 py-10 text-center ${
                  i > 0
                    ? "border-border border-t sm:border-t-0 sm:border-l"
                    : ""
                }`}
              >
                <stat.icon className={`h-6 w-6 ${stat.accent}`} />
                <span className="text-4xl font-bold text-white md:text-5xl">
                  <AnimatedCounter target={stat.target} suffix={stat.suffix} />
                </span>
                <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                  {stat.label}
                </span>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ── How it works ───────────────────────────────── */}
        <section className="px-6 py-20 md:py-28">
          <div className="mx-auto max-w-5xl">
            <Reveal className="mb-12">
              <h2 className="text-muted-foreground mb-3 text-sm font-bold tracking-wider uppercase">
                Cómo funciona
              </h2>
              <p className="max-w-lg text-2xl font-bold text-white md:text-3xl">
                Tres pasos para conectar con el fútbol
              </p>
            </Reveal>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {STEPS.map((step, i) => (
                <Reveal key={step.number} delay={i * 120}>
                  <div className="relative">
                    {/* Connecting dash between cards */}
                    {i < STEPS.length - 1 && (
                      <div className="border-krown-grey-65 absolute top-10 -right-3 hidden h-px w-6 border-t border-dashed md:block" />
                    )}
                    <div className="border-border bg-card group hover:border-krown-300 hover:shadow-krown-200/5 h-full rounded-lg border p-6 transition-all hover:-translate-y-1 hover:shadow-lg">
                      <div className="mb-4 flex items-center justify-between">
                        <span className="text-krown-grey-65 text-3xl font-bold">
                          {step.number}
                        </span>
                        <span
                          className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wider uppercase ${step.accentBg} ${step.accentBorder} ${step.accentText}`}
                        >
                          {step.badge}
                        </span>
                      </div>
                      <div
                        className={`mb-4 flex h-12 w-12 items-center justify-center rounded-lg ${step.accentBg}`}
                      >
                        <step.icon className={`h-6 w-6 ${step.accentText}`} />
                      </div>
                      <h3 className="mb-2 text-lg font-bold text-white">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Featured Players ───────────────────────────── */}
        <section className="border-border border-y px-6 py-20 md:py-28">
          <div className="mx-auto max-w-5xl">
            <Reveal className="mb-8">
              <h2 className="text-muted-foreground mb-3 text-sm font-bold tracking-wider uppercase">
                Jugadores Destacados
              </h2>
              <p className="text-2xl font-bold text-white md:text-3xl">
                Talento de toda Latinoamérica
              </p>
            </Reveal>

            <Reveal>
              <div className="scrollbar-hide flex gap-4 overflow-x-auto pb-4">
                {FEATURED_PLAYERS.map((player, i) => (
                  <div
                    key={player.name}
                    className={`border-border bg-card hover:border-krown-300 hover:shadow-krown-200/5 flex-shrink-0 rounded-lg border transition-all hover:-translate-y-1 hover:shadow-lg ${
                      i === 0
                        ? "max-w-[300px] min-w-[280px] p-5"
                        : "max-w-[240px] min-w-[220px] p-4"
                    }`}
                  >
                    <Avatar
                      className={i === 0 ? "mb-4 h-16 w-16" : "mb-3 h-12 w-12"}
                    >
                      <AvatarFallback
                        className={`bg-krown-200 font-bold text-white ${i === 0 ? "text-lg" : "text-sm"}`}
                      >
                        {player.initials}
                      </AvatarFallback>
                    </Avatar>
                    <p
                      className={`font-bold text-white ${i === 0 ? "text-base" : "text-sm"}`}
                    >
                      {player.name}
                    </p>
                    <p className="text-krown-100 text-xs">{player.position}</p>
                    <div className="text-krown-grey-50 mt-2 flex items-center gap-1 text-xs">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">
                        {player.city}, {player.country}
                      </span>
                    </div>
                    <div className="border-border mt-3 flex items-center gap-3 border-t pt-3">
                      <div className="text-center">
                        <span className="block text-sm font-bold text-white">
                          {player.goals}
                        </span>
                        <span className="text-muted-foreground text-[10px] tracking-wider uppercase">
                          Goles
                        </span>
                      </div>
                      <div className="bg-border h-6 w-px" />
                      <div className="text-center">
                        <span className="block text-sm font-bold text-white">
                          {player.assists}
                        </span>
                        <span className="text-muted-foreground text-[10px] tracking-wider uppercase">
                          Asist.
                        </span>
                      </div>
                      <div className="text-krown-grey-50 ml-auto flex items-center gap-1 text-xs">
                        <Eye className="h-3 w-3" />
                        {player.views}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal className="mt-6 text-center" delay={200}>
              <Button variant="brand-outline" className="group gap-2" asChild>
                <Link href={signUpPath()}>
                  Ver todos los jugadores
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </Reveal>
          </div>
        </section>

        {/* ── Testimonials ───────────────────────────────── */}
        <section className="px-6 py-20 md:py-28">
          <div className="mx-auto max-w-5xl">
            <Reveal className="mb-12 text-center">
              <h2 className="text-muted-foreground mb-3 text-sm font-bold tracking-wider uppercase">
                Testimonios
              </h2>
              <p className="text-2xl font-bold text-white md:text-3xl">
                Lo que dicen nuestros usuarios
              </p>
            </Reveal>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {TESTIMONIALS.map((t, i) => (
                <Reveal key={t.name} delay={i * 120}>
                  <div className="border-border bg-card hover:border-krown-300 flex h-full flex-col rounded-lg border p-6 transition-all hover:-translate-y-0.5">
                    <span className="text-krown-200 mb-4 text-3xl leading-none">
                      &ldquo;
                    </span>
                    <p className="text-krown-grey-30 mb-6 flex-1 text-sm leading-relaxed">
                      {t.quote}
                    </p>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-krown-200 text-xs font-bold text-white">
                          {t.initials}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-semibold text-white">
                          {t.name}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {t.role}
                        </p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Final CTA ──────────────────────────────────── */}
        <section className="border-border bg-card relative overflow-hidden border-t px-6 py-20 md:py-28">
          <div className="pointer-events-none absolute inset-0">
            <div className="bg-krown-200 absolute top-1/2 left-1/2 h-[400px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.08] blur-[100px]" />
            <div className="bg-krown-crown absolute right-0 bottom-0 h-[200px] w-[200px] rounded-full opacity-[0.06] blur-[80px]" />
          </div>

          <Reveal className="relative mx-auto max-w-2xl text-center">
            <Trophy className="text-krown-crown mx-auto mb-6 h-10 w-10" />
            <h2 className="mb-4 text-3xl font-bold text-white md:text-4xl">
              Tu carrera no puede esperar
            </h2>
            <p className="text-muted-foreground mb-8 text-lg">
              Unite a miles de jugadores, scouts y clubes que ya están
              conectando en KrownFT. Es gratis y podés empezar ahora.
            </p>
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Button
                size="lg"
                variant="cta"
                className="group gap-2 px-8 text-base"
                asChild
              >
                <Link href={signUpPath()}>
                  Crear cuenta gratis
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="ghost"
                className="text-muted-foreground hover:text-white"
                asChild
              >
                <Link href={signInPath()}>Ya tengo cuenta</Link>
              </Button>
            </div>
          </Reveal>
        </section>

        {/* ── Footer ─────────────────────────────────────── */}
        <footer className="border-border border-t px-6 py-10">
          <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 md:flex-row md:justify-between">
            <img
              src="/images/krown_logo_header.png"
              alt="KrownFT"
              className="h-6 opacity-60"
            />
            <div className="text-muted-foreground flex flex-wrap justify-center gap-6 text-sm">
              <Link
                href="/terminos"
                className="transition-colors hover:text-white"
              >
                Términos y Condiciones
              </Link>
              <Link
                href="/privacidad"
                className="transition-colors hover:text-white"
              >
                Privacidad
              </Link>
              <Link
                href="/cookies"
                className="transition-colors hover:text-white"
              >
                Cookies
              </Link>
            </div>
            <p className="text-krown-grey-50 text-xs">
              &copy; {new Date().getFullYear()} KrownFT. Todos los derechos
              reservados.
            </p>
          </div>
        </footer>
      </div>
    </>
  )
}
